import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { PlantCard } from "@/components/PlantCard";
import { PlantDialog } from "@/components/PlantDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Sprout } from "lucide-react";

export default function Sprouts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plants, setPlants] = useState(null);
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/plants");
    setPlants(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = () => {
    if (!user?.is_premium && plants && plants.length >= 3) navigate("/pricing");
    else setDialogOpen(true);
  };

  const filtered = (plants || []).filter((p) =>
    [p.name, p.species, p.variety, p.location].filter(Boolean).join(" ").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">My Sprouts</h1>
          <p className="text-muted-foreground mt-1">{plants ? `${plants.length} in your garden` : "Loading…"}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search sprouts…" className="pl-9 w-full sm:w-56" data-testid="sprout-search-input" />
          </div>
          <Button onClick={handleAdd} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl shrink-0" data-testid="add-plant-button">
            <Plus className="h-4 w-4 mr-1.5" /> Add
          </Button>
        </div>
      </div>

      {!plants ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-72 rounded-2xl bg-card border border-border animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-2xl border border-dashed border-border p-12 text-center">
          <Sprout className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-muted-foreground">{query ? "No sprouts match your search." : "No sprouts yet. Add your first!"}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="sprouts-grid">
          {filtered.map((p) => (
            <PlantCard key={p.id} plant={p} onChanged={load} />
          ))}
        </div>
      )}

      <PlantDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} />
    </Layout>
  );
}
